import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FaCog, FaTimes } from "react-icons/fa";
import {
  getDefaultVisibleColumnKeys,
  getToggleableColumns,
} from "./commissionsColumnPrefs";

function CommissionsColumnSettings({
  report = "general",
  visibleKeys = [],
  onChange,
}) {
  const [open, setOpen] = useState(false);
  const [draftKeys, setDraftKeys] = useState(visibleKeys);
  const toggleableColumns = getToggleableColumns(report);
  const visibleSet = new Set(draftKeys);

  useEffect(() => {
    if (!open) return;
    setDraftKeys(visibleKeys);
  }, [open, visibleKeys]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const handleToggle = (key, checked) => {
    setDraftKeys((prev) =>
      checked ? [...prev, key] : prev.filter((item) => item !== key),
    );
  };

  const handleReset = () => {
    setDraftKeys(getDefaultVisibleColumnKeys(report));
  };

  const handleApply = () => {
    onChange?.(draftKeys);
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 text-[12px] font-medium text-gray-700 transition hover:border-gray-300 hover:bg-gray-50"
        style={{ boxShadow: "none" }}
        aria-haspopup="dialog"
      >
        <FaCog size={12} className="text-gray-500" />
        Columns
      </button>

      {open
        ? createPortal(
            <>
              <div
                className="cms-app-modal-backdrop"
                style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
                onClick={handleClose}
                aria-hidden="true"
              />
              <div
                className="cms-app-modal cms-app-modal--centered flex items-center justify-center p-3 sm:p-4"
                role="dialog"
                aria-modal="true"
                aria-labelledby="commissions-columns-modal-title"
              >
                <div className="w-full max-w-md overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
                  <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3 sm:px-5">
                    <div className="min-w-0">
                      <h2
                        id="commissions-columns-modal-title"
                        className="mb-0 text-[15px] font-semibold text-gray-900"
                      >
                        Table columns
                      </h2>
                      <p className="mb-0 mt-1 text-[12px] text-gray-500">
                        Select which columns to show in this report.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleClose}
                      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
                      aria-label="Close column settings"
                      style={{ border: "none", background: "transparent" }}
                    >
                      <FaTimes size={14} />
                    </button>
                  </div>

                  <div className="max-h-[50vh] overflow-y-auto px-4 py-3 sm:px-5">
                    <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                      {toggleableColumns.map((column) => {
                        const checked = visibleSet.has(column.key);
                        return (
                          <label
                            key={column.key}
                            className="flex cursor-pointer items-center gap-2 rounded-lg border border-transparent px-2 py-2 text-[13px] text-gray-700 transition hover:border-gray-100 hover:bg-gray-50"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(event) =>
                                handleToggle(column.key, event.target.checked)
                              }
                              className="rounded border-gray-300 text-[#007BFF] focus:ring-[#007BFF]/30"
                            />
                            <span>{column.title}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 px-4 py-3 sm:px-5">
                    <button
                      type="button"
                      onClick={handleReset}
                      className="text-[12px] font-medium text-[#007BFF] hover:underline"
                      style={{ border: "none", background: "transparent", padding: 0 }}
                    >
                      Reset to defaults
                    </button>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="inline-flex h-9 items-center rounded-md border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-700 hover:bg-gray-50"
                        style={{ boxShadow: "none" }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleApply}
                        className="inline-flex h-9 items-center rounded-md bg-[#007BFF] px-3 text-[13px] font-semibold text-white hover:bg-[#0069D9]"
                        style={{ border: "none", boxShadow: "none" }}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>,
            document.body,
          )
        : null}
    </>
  );
}

export default CommissionsColumnSettings;
